/**
 * SSO Signup - 회원가입 폼 화면
 * 
 * 정보 제공 동의 후 메디크루 계정을 생성하는 화면
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, AlertDialog } from '@/components/shared/ui';
import { getSsoInfo, ssoSignup, ssoLogin } from '@/services/ssoService';
import { useAuth } from '@/contexts/AuthContext';
import { useUser } from '@/data/queries/useUser';
import { convertRoleNumberToString, resolveLicenseFromProfile } from '@/utils/roleUtils';
import { ROUTES } from '@/lib/constants/routes';
import type { UserSsoAccessDto } from '@/types/api/sso';

const medicrewLogo = '/assets/images/brand/medicrew_blue_logo.png';

export default function SsoSignupForm(): React.ReactElement {
  const { origin, userSsoAccessId } = useParams<{ origin: string; userSsoAccessId: string }>();
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const { getProfile } = useUser();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userSsoAccess, setUserSsoAccess] = useState<UserSsoAccessDto | null>(null);
  
  // 폼 데이터
  const [realName, setRealName] = useState('');
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);

  const [alertState, setAlertState] = useState<{ isOpen: boolean; message: string }>({
    isOpen: false,
    message: '',
  });

  const showAlert = (message: string) => {
    setAlertState({ isOpen: true, message });
  };

  const closeAlert = () => {
    setAlertState({ isOpen: false, message: '' });
  };

  useEffect(() => {
    const loadSsoInfo = async () => {
      if (!origin || origin !== 'kha') {
        showAlert('지원하지 않는 SSO 제공자입니다.');
        setIsLoading(false);
        return;
      }

      if (!userSsoAccessId || isNaN(Number(userSsoAccessId))) {
        showAlert('잘못된 SSO 접근 정보입니다.');
        setIsLoading(false);
        return;
      }

      const userSsoAccessIdNum = Number(userSsoAccessId);

      try {
        const ssoInfo = await getSsoInfo(userSsoAccessIdNum);
        setUserSsoAccess(ssoInfo.userSsoAccess);
        setIsLoading(false);
      } catch (error: any) {
        showAlert(error.message || 'SSO 정보를 불러오는데 실패했습니다.');
        setIsLoading(false);
      }
    };

    loadSsoInfo();
  }, [origin, userSsoAccessId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userSsoAccess) return;

    // 유효성 검사
    if (!realName.trim()) {
      showAlert('이름을 입력해주세요.');
      return;
    }

    if (!loginId.trim()) {
      showAlert('아이디를 입력해주세요.');
      return;
    }

    if (loginId.length < 4) {
      showAlert('아이디는 최소 4자 이상이어야 합니다.');
      return;
    }

    if (!password) {
      showAlert('비밀번호를 입력해주세요.');
      return;
    }

    if (password.length < 8) {
      showAlert('비밀번호는 최소 8자 이상이어야 합니다.');
      return;
    }

    if (password !== passwordConfirm) {
      showAlert('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (!agreeTerms) {
      showAlert('서비스 이용약관 및 개인정보처리방침에 동의해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. 회원가입
      await ssoSignup({
        userSsoAccessId: userSsoAccess.id,
        email: userSsoAccess.email,
        realName: realName.trim(),
        loginId: loginId.trim(),
        pw: password,
      });

      // 2. 자동 로그인
      const loginResponse = await ssoLogin({
        userSsoAccessId: userSsoAccess.id,
      });

      // 3. 토큰 저장
      localStorage.setItem('medicrew_access_token', loginResponse.accessToken);
      if (loginResponse.refreshToken?.trim()) {
        localStorage.setItem('medicrew_refresh_token', loginResponse.refreshToken.trim());
      } else {
        localStorage.removeItem('medicrew_refresh_token');
      }

      // 4. 프로필 조회 & 로그인 처리
      const userProfile = await getProfile();

      const userRole = convertRoleNumberToString(userProfile.role);
      const userLicense = resolveLicenseFromProfile(userProfile);

      const organizationId = userProfile.organizationId != null ? String(userProfile.organizationId) : null;
      const organizationName = userProfile.organizationName?.trim() ?? '';

      const displayName =
        userProfile.realName && userProfile.realName.trim() ? userProfile.realName.trim() : loginId;

      const currentAccountEntry = {
        accountId: `acc_${userProfile.userId}`,
        loginId: loginId.trim(),
        displayName,
        email: userProfile.email || '',
        profileImageUrl: userProfile.profileImageUrl,
        phoneNumber: userProfile.phoneNumber?.trim() ?? '',
        affiliation: userProfile.affiliation?.trim() ?? '',
        position: userProfile.position?.trim() ?? '',
        department: userProfile.department?.trim() ?? '',
        organizationId,
        organizationName,
        role: userRole,
        license: userLicense,
        isActive: true,
      };

      let accounts: typeof currentAccountEntry[] = [currentAccountEntry];

      try {
        const storedAccounts = localStorage.getItem('medicrew_accounts');
        if (storedAccounts) {
          const parsed = JSON.parse(storedAccounts) as typeof currentAccountEntry[];
          const filteredAccounts = parsed.filter(
            (acc) => acc.accountId !== currentAccountEntry.accountId
          );
          accounts = [currentAccountEntry, ...filteredAccounts];
        }
      } catch {
        // localStorage 파싱 실패 시 현재 계정만 사용
      }

      const guestAccountExpiresAt =
        userRole === 'guest' ? (userProfile.guestAccountExpiresAt ?? null) : null;

      const userData = {
        userId: userProfile.userId,
        name: displayName,
        email: userProfile.email || '',
        profileImageUrl: userProfile.profileImageUrl,
        guestAccountExpiresAt,
        isAuthenticated: true,
        currentAccount: {
          accountId: currentAccountEntry.accountId,
          loginId: currentAccountEntry.loginId,
          displayName: currentAccountEntry.displayName,
          email: currentAccountEntry.email,
          profileImageUrl: currentAccountEntry.profileImageUrl,
          phoneNumber: currentAccountEntry.phoneNumber,
          affiliation: currentAccountEntry.affiliation,
          position: currentAccountEntry.position,
          department: currentAccountEntry.department,
          organizationId: currentAccountEntry.organizationId,
          organizationName: currentAccountEntry.organizationName,
          role: currentAccountEntry.role,
          license: currentAccountEntry.license,
        },
        accounts,
      };
      authLogin(userData);

      // 5. 리다이렉트
      if (userSsoAccess.redirect?.classId) {
        const classId = userSsoAccess.redirect.classId;
        navigate(`/open-class/${classId}`);
      } else {
        navigate(ROUTES.HOME);
      }
    } catch (error: any) {
      showAlert(error.message || '회원가입에 실패했습니다.');
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.HOME);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent mb-4"></div>
          <p className="text-gray-600 text-lg">정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!userSsoAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <AlertDialog isOpen={alertState.isOpen} onClose={closeAlert} message={alertState.message} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-[520px] bg-white rounded-2xl p-10 shadow-[0_15px_40px_-10px_rgba(0,0,0,0.08)] border border-gray-100">
        <div className="flex justify-center mb-6">
          <img src={medicrewLogo} alt="Medicrew" className="h-7 w-auto" />
        </div>

        <h1 className="text-xl font-bold text-gray-900 mb-2 text-center">
          메디크루 계정 만들기
        </h1>

        <p className="text-sm text-gray-600 text-center mb-8">
          대한병원협회 정보로 메디크루 계정을 생성합니다. 앱 로그인에 사용할
          아이디와 비밀번호를 설정해 주세요.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* 이메일 (읽기 전용) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              이메일
            </label>
            <div className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-lg text-gray-700 text-sm">
              {userSsoAccess.email}
            </div>
          </div>

          {/* 이름 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              이름
            </label>
            <input
              type="text"
              placeholder="이름"
              value={realName}
              onChange={(e) => setRealName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
              disabled={isSubmitting}
            />
          </div>

          {/* 새 아이디 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              새 아이디
            </label>
            <input
              type="text"
              placeholder="사용할 아이디를 입력해주세요"
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
              disabled={isSubmitting}
            />
            <p className="text-xs text-gray-500 mt-1">
              영문, 숫자, '.', '_', '-' 만 사용할 수 있으며 최소 4자입니다.
            </p>
          </div>

          {/* 비밀번호 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              비밀번호
            </label>
            <input
              type="password"
              placeholder="비밀번호"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
              disabled={isSubmitting}
            />
            <p className="text-xs text-gray-500 mt-1">
              영문자, 숫자 포함 최소 8자 이상 (대소문자, 특수문자 혼용 시 더 강력함)
            </p>
          </div>

          {/* 비밀번호 확인 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              비밀번호 확인
            </label>
            <input
              type="password"
              placeholder="비밀번호 확인"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
              disabled={isSubmitting}
            />
          </div>

          {/* 약관 동의 */}
          <div className="flex items-start gap-2 pt-2">
            <input
              type="checkbox"
              id="agreeTerms"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-brand-500 border-gray-300 rounded focus:ring-brand-500"
              disabled={isSubmitting}
            />
            <label htmlFor="agreeTerms" className="text-sm text-gray-700">
              <span className="text-brand-500 underline cursor-pointer">서비스 이용약관</span> 및{' '}
              <span className="text-brand-500 underline cursor-pointer">개인정보처리방침</span>에 동의합니다.
            </label>
          </div>

          {/* 제출 버튼 */}
          <div className="space-y-2 pt-3">
            <Button
              type="submit"
              className="w-full rounded-full text-base h-12"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? '계정 생성 중...' : '계정 만들고 로그인'}
            </Button>

            <button
              type="button"
              className="w-full text-gray-500 hover:text-gray-900 transition-colors text-sm py-2"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              취소
            </button>
          </div>
        </form>
      </div>

      <div className="mt-7 flex justify-center items-center gap-2">
        <span className="text-xs text-gray-400">Powered by</span>
        <span className="text-xs font-bold text-gray-500">NEWBASE</span>
      </div>

      <AlertDialog isOpen={alertState.isOpen} onClose={closeAlert} message={alertState.message} />
    </div>
  );
}
